import React, { JSX } from "react";
import { useParams } from "react-router";
import { sitemap } from "sitemap";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { Button, ButtonType } from "components/buttons/button/button";
import { t } from "utilities/localization/t";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import "./user-contracts-ill-list-page.scss";
import { EventService } from "utilities/services/events/event-service";
import { useGetOrganizationEventSummary } from "data/data-access-hooks/organizations/organization-queries";
import OrganizationEventListAccordion from "./organization-event-list-accordion";
import { Heading, HeadingPriority, HeadingSize } from "components/typography/heading/heading";
import DisplayCard from "../../providers/user-providers-event-history-page/display-card";
import { Spinner } from "components/spinner/spinner";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface UserContractsILLListPageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "user-contracts-ill-list-page";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const UserContractsILLListPage: React.FC<UserContractsILLListPageProps> = validatePageAccess(
    AccessControlKeys.UserContractsILLListPage
)((): JSX.Element => {
    const routeParams = useParams();
    const orgId = routeParams.id;

    const { list: organizationEventSummaryList } = EventService.useGetOrganizationEventSummaryList();

    const { data: summaryData, isLoading: isLoadingSummary } = useGetOrganizationEventSummary(
        organizationEventSummaryList,
        parseInt(orgId ?? "0")
    );

    const headingTotalEvents = summaryData?.reduce((acc, curr) => acc + (curr.totalEvents ?? 0), 0);

    return (
        <div className={CSS_CLASS_NAME}>
            <div className="content-wrap">
                <div className="content">
                    <SkipNavContent>
                        <div className={`${CSS_CLASS_NAME}__header`}>
                        <Heading priority={HeadingPriority.H2} size={HeadingSize.XSmall}>
                            {t("eventHistory")}
                        </Heading>
                        <div className={`${CSS_CLASS_NAME}__header__actions`}>
                            <div className={`${CSS_CLASS_NAME}__header__actions__search`}>
                            </div>
                            <Button
                                linkPath={`${sitemap.admin.event.list}?organizationId=${encodeURIComponent(orgId ?? "")}`}
                                text={t("goToEvents")}
                                type={ButtonType.Link}
                            />
                            </div>
                        </div>
                         {isLoadingSummary ? (
                                <div className="flex justify-center items-center h-80">
                                    <Spinner />
                                </div>
                            ) : (
                                <>
                                    <div className="flex space-x-4">
                                        <DisplayCard
                                            title={t("totalEvents")}
                                            value={(headingTotalEvents ?? 0).toString()}
                                        />
                                    </div>
                                    <div className="mt-5">
                                    {summaryData?.map(summaryYear => (
                                        <OrganizationEventListAccordion
                                            key={summaryYear.year}
                                            organizationId={parseInt(orgId ?? "0")}
                                            summary={summaryYear}
                                        />
                                    ))}
                                    </div>  
                                </>
                            )}
                    </SkipNavContent>
                </div>
            </div>
        </div>
    );
});

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { UserContractsILLListPage };

// #endregion Exports
